// client/src/components/pages/AmendmentsAllocateTransactions/BulkAmendmentModalAllocateTransactions.tsx
// (converted from .js, ST-00221). Bulk Amend: one set of field values for every selected ticket.
import { useState, useEffect, useMemo } from 'react';
import Select, { type StylesConfig } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import type { AxiosResponse } from 'axios';
import api from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import TicketAttachments from '../../common/TicketAttachments';
import { isValidWeight, isWeightField, rejectionMessage } from './amendmentFields';
import type {
  AmendmentRejectionBody,
  AmendmentTicketRow,
  BulkAmendPayload,
  BulkAmendResponse,
  PendingBatchConflictBody,
} from '../../../types/clientPages/amendments';

export interface BulkAmendmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedTickets: AmendmentTicketRow[];
  onSaveSuccess: () => void;
}

interface LocationOption {
  value: string;
  label: string;
}

/** A ticket the server did not save, as the modal lists it. */
interface UnsavedTicket {
  ticketUID: string;
  ticketNo: string;
  error?: string | null;
}

/** What a save came back with when not every ticket was saved (AMD-20). */
interface SaveOutcome {
  message: string;
  unsaved: UnsavedTicket[];
  /** Some tickets were saved (HTTP 207): the page must refresh when the modal closes. */
  savedSome: boolean;
}

type RequestError = Error & {
  response?: {
    status?: number;
    data?: AmendmentRejectionBody & PendingBatchConflictBody;
  };
};

const LOCATION_FIELDS: ReadonlySet<string> = new Set(['Dispatch Location', 'Receipt Location']);
// The editable weigh timestamps (a date-time input).
const TIMESTAMP_FIELDS: ReadonlySet<string> = new Set([
  'First Weight Timestamp',
  'Second Weight Timestamp',
]);

// Match the surrounding input height/styling so the Select sits flush with other fields
const locationSelectStyles: StylesConfig<LocationOption, false> = {
  control: (base, state) => ({
    ...base,
    minHeight: '34px',
    backgroundColor: 'var(--surface)',
    borderColor: state.isFocused ? 'var(--accent)' : 'var(--border)',
    boxShadow: state.isFocused ? '0 0 0 2px rgb(var(--accent-rgb) / 0.25)' : 'none',
    '&:hover': { borderColor: state.isFocused ? 'var(--accent)' : 'var(--border-strong)' },
    fontSize: '0.875rem',
  }),
  valueContainer: (base) => ({ ...base, padding: '0 8px' }),
  input: (base) => ({ ...base, margin: 0, padding: 0, color: 'var(--text-strong)' }),
  singleValue: (base) => ({ ...base, color: 'var(--text-strong)' }),
  placeholder: (base) => ({ ...base, color: 'var(--text-faint)' }),
  indicatorsContainer: (base) => ({ ...base, height: '32px' }),
  menu: (base) => ({
    ...base,
    zIndex: 60,
    fontSize: '0.875rem',
    backgroundColor: 'var(--surface-raised)',
    border: '1px solid var(--border)',
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? 'var(--accent)'
      : state.isFocused
        ? 'var(--accent-soft)'
        : 'var(--surface-raised)',
    color: state.isSelected ? 'var(--text-on-accent)' : 'var(--text-strong)',
    cursor: 'pointer',
  }),
  multiValue: (base) => ({ ...base, backgroundColor: 'var(--accent-soft)' }),
  multiValueLabel: (base) => ({ ...base, color: 'var(--accent-text)', fontWeight: 500 }),
  multiValueRemove: (base) => ({
    ...base,
    color: 'var(--accent-text)',
    ':hover': { backgroundColor: 'var(--accent)', color: 'var(--text-on-accent)' },
  }),
};

// Helper function to format date for input type="datetime-local"
const formatDateTimeLocal = (dateString: string): string => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    // Format: YYYY-MM-DDTHH:mm (No seconds needed for bulk usually)
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  } catch (error) {
    return '';
  }
};

// The fields a bulk amendment can change: the amendable columns of the data view (as the
// server's AMENDABLE_FIELDS). ImportTimestamp and ImportSource are not among them — the server
// dropped them silently and wrote an empty version (AMD-19) — so they are not offered.
// Not offered either: TicketUID, Version, Ticket No, Ticket Timestamp and the amendment
// stamps (AmendmentNotes is the notes box below).
const POTENTIALLY_EDITABLE_FIELDS = [
  'Delivery No',
  'Order No',
  'Revision No',
  'Ticket Type',
  'Dispatch Organisation',
  'Dispatch Location',
  'Receipt Organisation',
  'Receipt Location',
  'First Weight',
  'Second Weight',
  'Nett Weight',
  'Delivery Weight',
  'Variance Weight',
  'Haulier',
  'Reg No',
  'Driver',
  'Product',
  'Ref No',
  'Product Ref.',
  'Trailer Reg 1',
  'Trailer Reg 2',
  'Security Seal 1',
  'Security Seal 2',
  'First Weight Timestamp',
  'Second Weight Timestamp',
  'Time Between Weights',
  // Tag fields for manual data entry
  'Tag1',
  'Tag2',
  'Tag3',
  'Tag4',
];

// Fields surfaced in the single-ticket "current state" panel
const KEY_IDENTIFIER_FIELDS = [
  'Ticket No',
  'Ticket Timestamp',
  'Dispatch Location',
  'Receipt Location',
  'Haulier',
  'Reg No',
  'Product',
  'Nett Weight',
  'Delivery No',
  'Ref No',
];

const formatCurrentValue = (val: unknown): string => {
  if (val === null || val === undefined || val === '') return '—';
  if (val instanceof Date) return val.toLocaleString();
  if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(val)) {
    const d = new Date(val);
    if (!isNaN(d.getTime())) return d.toLocaleString();
  }
  return String(val);
};

interface ValueCount {
  display: string;
  count: number;
}

// Distribution of current values for a field across tickets
const summarizeFieldValues = (fieldName: string, tickets: AmendmentTicketRow[]): ValueCount[] => {
  const counts = new Map<string, ValueCount>();
  tickets.forEach((t) => {
    const raw = t?.[fieldName];
    const isBlank = raw === null || raw === undefined || raw === '';
    const key = isBlank ? '__BLANK__' : String(raw);
    let entry = counts.get(key);
    if (!entry) {
      entry = { display: isBlank ? '(blank)' : formatCurrentValue(raw), count: 0 };
      counts.set(key, entry);
    }
    entry.count += 1;
  });
  return Array.from(counts.values()).sort((a, b) => b.count - a.count);
};

const toOptions = (data: unknown): LocationOption[] =>
  (Array.isArray(data) ? data : [])
    .filter((v): v is string => typeof v === 'string' && v !== '')
    .map((v) => ({ value: v, label: v }));

// "Corrected A", "Corrected A and Corrected B", "Corrected A, Corrected B, and Corrected C"
const generateAutoComment = (selectedFields: string[]): string => {
  if (selectedFields.length === 0) return '';
  const correctedFields = selectedFields.map((field) => `Corrected ${field}`);
  if (correctedFields.length === 1) return correctedFields[0]!;
  if (correctedFields.length === 2) return correctedFields.join(' and ');
  const lastField = correctedFields.pop();
  return correctedFields.join(', ') + ', and ' + lastField;
};

const BulkAmendmentModalAllocateTransactions = ({
  isOpen,
  onClose,
  selectedTickets,
  onSaveSuccess,
}: BulkAmendmentModalProps) => {
  const { hasAppRole } = useAuth();
  // Only a global admin may name a location that does not exist yet; everyone else picks a
  // known one. The server refuses an unknown location (or one that would move the ticket out
  // of the caller's view) for anyone else (AMD-23).
  const canCreateLocation = hasAppRole('global-admin');

  const [fieldsToUpdate, setFieldsToUpdate] = useState<Record<string, string>>({});
  const [amendmentNotes, setAmendmentNotes] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<SaveOutcome | null>(null);
  const [dispatchLocationOptions, setDispatchLocationOptions] = useState<LocationOption[]>([]);
  const [receiptLocationOptions, setReceiptLocationOptions] = useState<LocationOption[]>([]);
  const [locationsLoading, setLocationsLoading] = useState(false);

  // Derive the ticket type to scope dropdowns with.
  // - All selected rows same type → scope to that type
  // - Mixed types → null (unscoped, show all locations)
  const scopedTicketType = useMemo((): string | null => {
    if (!selectedTickets || selectedTickets.length === 0) return null;
    const types = new Set(
      selectedTickets
        .map((t) => t && t['Ticket Type'])
        .filter((v): v is string => v !== null && v !== undefined && v !== '')
    );
    return types.size === 1 ? Array.from(types)[0]! : null;
  }, [selectedTickets]);

  useEffect(() => {
    // Reset state when modal opens
    if (isOpen) {
      setFieldsToUpdate({});
      setAmendmentNotes('');
      setError(null);
      setSuccessMessage(null);
      setOutcome(null);
    }
  }, [isOpen]);

  // Lazy-load location options only once a location field is toggled on
  const needsLocationOptions =
    Object.hasOwn(fieldsToUpdate, 'Dispatch Location') ||
    Object.hasOwn(fieldsToUpdate, 'Receipt Location');

  useEffect(() => {
    if (!isOpen || !needsLocationOptions) return;
    let cancelled = false;
    setLocationsLoading(true);
    Promise.all([
      api.data.getDispatchLocations(scopedTicketType || undefined),
      api.data.getReceiptLocations(undefined, scopedTicketType || undefined),
    ])
      .then(([dispatchRes, receiptRes]: [AxiosResponse<unknown>, AxiosResponse<unknown>]) => {
        if (cancelled) return;
        setDispatchLocationOptions(toOptions(dispatchRes.data));
        setReceiptLocationOptions(toOptions(receiptRes.data));
      })
      .catch((err: unknown) => {
        console.error('Failed to load location options:', err);
        if (!cancelled) {
          setDispatchLocationOptions([]);
          setReceiptLocationOptions([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLocationsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, needsLocationOptions, scopedTicketType]);

  const handleFieldChange = (fieldName: string, value: string) => {
    setFieldsToUpdate((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  const handleToggleField = (fieldName: string) => {
    setFieldsToUpdate((prev) => {
      const newState = { ...prev };
      if (Object.hasOwn(newState, fieldName)) {
        delete newState[fieldName]; // Remove field if it exists (untoggle)
      } else {
        // Every field starts empty when toggled on. Weight fields intentionally start empty
        // (NOT 0): a numeric default gets saved as a real 0.00 weight if the typed value never
        // registers (e.g. a comma decimal makes the number input report '').
        newState[fieldName] = '';
      }

      // Auto-generate comment based on selected fields
      setAmendmentNotes(generateAutoComment(Object.keys(newState)));

      return newState;
    });
  };

  const ticketNoOf = (uid: string): string => {
    const t = selectedTickets.find((row) => row.TicketUID === uid);
    return t?.['Ticket No'] || uid;
  };
  const unsavedList = (failed: { ticketUID: string; error?: string | null }[]): UnsavedTicket[] =>
    failed.map((f) => ({
      ticketUID: f.ticketUID,
      ticketNo: ticketNoOf(f.ticketUID),
      error: f.error,
    }));

  // Closing after a partial save refreshes the page: some tickets did change.
  const handleClose = () => {
    if (outcome?.savedSome) onSaveSuccess();
    onClose();
  };

  const handleSave = async () => {
    if (Object.keys(fieldsToUpdate).length === 0) {
      setError('Please select at least one field to update.');
      return;
    }
    if (!amendmentNotes.trim()) {
      setError('Amendment Notes are compulsory for bulk updates.');
      return;
    }
    if (!selectedTickets || selectedTickets.length === 0) {
      setError('No tickets selected for bulk amendment.');
      return;
    }

    // Weight fields must carry a valid number — an empty/invalid entry would otherwise be
    // saved as NULL (the source weight shows again). The weight columns are listed explicitly:
    // Time Between Weights ('0:04:22') and the weigh timestamps are not weights (AMD-19).
    const badWeightFields = Object.entries(fieldsToUpdate)
      .filter(([fieldName, value]) => isWeightField(fieldName) && !isValidWeight(value))
      .map(([fieldName]) => fieldName);
    if (badWeightFields.length > 0) {
      setError(
        `Please enter a valid number for: ${badWeightFields.join(', ')} (or untick the field). Use a point as decimal separator, e.g. 51.35`
      );
      return;
    }

    setIsLoading(true);
    setError(null);
    setOutcome(null);

    const payload: BulkAmendPayload = {
      ticketUIDs: selectedTickets.map((t) => t.TicketUID),
      updates: fieldsToUpdate,
      amendmentNotes: amendmentNotes.trim(),
    };

    try {
      const response: AxiosResponse<BulkAmendResponse> =
        await api.amendmentsV3.bulkAmendTickets(payload);
      const data = response.data || {};

      // HTTP 207: some tickets were not saved. Say which (and why) and stay open, instead of
      // closing as if every ticket had saved (AMD-20).
      const failed = Array.isArray(data.failed) ? data.failed : [];
      if (failed.length > 0) {
        setOutcome({
          message: data.message || 'Some tickets were not saved.',
          unsaved: unsavedList(failed),
          savedSome: true,
        });
        return;
      }

      if (data.requiresApproval) {
        setSuccessMessage(data.message || 'Amendment submitted for approval');
        setTimeout(() => {
          onSaveSuccess();
          onClose();
        }, 2000);
        return;
      }

      onSaveSuccess();
      onClose();
    } catch (caught) {
      const err = caught as RequestError;
      console.error('Error saving bulk amendment:', err);
      const body = err.response?.data;
      if (err.response?.status === 409) {
        const conflicts = body?.conflicts || [];
        const extra =
          conflicts.length > 0
            ? ` Existing batch(es): ${conflicts.map((c) => `#${c.approvalId} by ${c.submittedBy}`).join(', ')}.`
            : '';
        setError(`${body?.message}${extra} Go to Amendment Approvals to withdraw or reject first.`);
      } else if (Array.isArray(body?.failed) && body!.failed.length > 0) {
        // None saved (404/500 { message, failed }): name the tickets; the update can be retried.
        setOutcome({
          message: body!.message || 'No ticket was saved.',
          unsaved: unsavedList(body!.failed),
          savedSome: false,
        });
      } else {
        setError(rejectionMessage(body) || err.message || 'Failed to save bulk amendment.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  const ticketCount = selectedTickets?.length || 0;
  const fieldsSelectedCount = Object.keys(fieldsToUpdate).length;
  const canSubmit = !isLoading && fieldsSelectedCount > 0 && !!amendmentNotes.trim();
  const partiallySaved = !!outcome?.savedSome;

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto bg-gray-900/60 dark:bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fadeIn"
      onClick={handleClose}
    >
      <div
        className="bg-surface rounded-xl shadow-2xl ring-1 ring-gray-900/5 w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-7 py-5 border-b border-gray-200 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h3 className="font-display text-lg font-semibold tracking-tight text-gray-900 leading-tight">
              Bulk Amend
            </h3>
            <p className="text-xs text-gray-500 mt-1 tracking-wide">Allocate Transactions</p>
          </div>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-50 ring-1 ring-inset ring-primary-200 px-2.5 py-1 text-xs font-medium text-primary-700 tabular-nums whitespace-nowrap flex-shrink-0">
            <span className="h-1.5 w-1.5 rounded-full bg-primary-500" aria-hidden="true"></span>
            {ticketCount} ticket{ticketCount === 1 ? '' : 's'}
          </span>
        </div>

        {/* Body */}
        <div className="px-7 py-6 flex-1 overflow-y-auto space-y-7">
          {successMessage && (
            <div className="rounded-md bg-amber-50 border border-amber-200 px-3.5 py-2.5 text-sm text-amber-800 flex items-center gap-2">
              <svg
                className="w-4 h-4 flex-shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              {successMessage}
            </div>
          )}
          {error && (
            <div className="rounded-md bg-red-50 border border-red-200 px-3.5 py-2.5 text-sm text-red-700">
              {error}
            </div>
          )}
          {outcome && (
            <div
              role="alert"
              className={`rounded-md border px-3.5 py-2.5 text-sm ${
                outcome.savedSome
                  ? 'bg-amber-50 border-amber-200 text-amber-800'
                  : 'bg-red-50 border-red-200 text-red-700'
              }`}
            >
              <p className="font-medium">{outcome.message}</p>
              <p className="mt-1.5 text-xs">
                {outcome.savedSome ? 'The other tickets were saved. Not saved:' : 'Not saved:'}
              </p>
              <ul className="mt-1 max-h-40 overflow-y-auto space-y-0.5 text-xs">
                {outcome.unsaved.map((t) => (
                  <li key={t.ticketUID} className="font-mono">
                    {t.error ? `${t.ticketNo} — ${t.error}` : t.ticketNo}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Current state — single ticket */}
          {ticketCount === 1 && (
            <section className="rounded-lg border border-gray-200 bg-gray-50/70 px-5 py-4">
              <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-500 mb-3">
                Current state
              </div>
              <dl className="grid grid-cols-2 gap-x-8 gap-y-3.5">
                {KEY_IDENTIFIER_FIELDS.map((f) => {
                  const v = formatCurrentValue(selectedTickets[0]?.[f]);
                  return (
                    <div key={f} className="min-w-0">
                      <dt className="text-[10px] font-medium uppercase tracking-[0.1em] text-gray-400 mb-0.5">
                        {f}
                      </dt>
                      <dd
                        className="text-sm font-medium text-gray-900 truncate tabular-nums"
                        title={v}
                      >
                        {v}
                      </dd>
                    </div>
                  );
                })}
              </dl>
            </section>
          )}

          {/* Selected tickets list — multi */}
          {ticketCount > 1 && (
            <details className="group rounded-lg border border-gray-200 bg-gray-50/70 overflow-hidden">
              <summary className="list-none cursor-pointer px-5 py-3 flex items-center gap-2.5 text-sm text-gray-700 hover:bg-gray-100/70 transition-colors select-none [&::-webkit-details-marker]:hidden">
                <svg
                  className="h-3.5 w-3.5 text-gray-400 transition-transform duration-150 group-open:rotate-90"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="font-medium">Selected tickets</span>
                <span className="ml-auto text-xs text-gray-500 tabular-nums">{ticketCount}</span>
              </summary>
              <ul className="divide-y divide-gray-100 border-t border-gray-200 bg-surface max-h-52 overflow-y-auto">
                {selectedTickets.map((t) => (
                  <li
                    key={t.TicketUID}
                    className="px-5 py-2 flex items-baseline gap-3 text-xs min-w-0"
                  >
                    <span className="font-mono text-[11px] text-gray-900 flex-shrink-0">
                      {t['Ticket No'] || (t.TicketUID ? t.TicketUID.slice(0, 8) : '—')}
                    </span>
                    <span className="text-gray-500 truncate">
                      {t['Dispatch Location'] || '—'}
                      <span className="text-gray-300 mx-1.5">→</span>
                      {t['Receipt Location'] || '—'}
                    </span>
                  </li>
                ))}
              </ul>
            </details>
          )}

          {/* Fields */}
          <section>
            <div className="flex items-baseline justify-between mb-3">
              <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-500">
                Fields
              </div>
              <p className="text-[11px] text-gray-400">
                {fieldsSelectedCount === 0
                  ? 'Tick a field to include it in the update'
                  : `${fieldsSelectedCount} field${fieldsSelectedCount === 1 ? '' : 's'} selected`}
              </p>
            </div>
            <div className="divide-y divide-gray-100 border-y border-gray-200">
              {POTENTIALLY_EDITABLE_FIELDS.map((fieldName) => {
                const isChecked = Object.hasOwn(fieldsToUpdate, fieldName);
                const value = isChecked ? fieldsToUpdate[fieldName]! : '';
                const label = fieldName.replace(/([A-Z])/g, ' $1').trim();
                const valueLabel = `New ${label.replace(/\s+/g, ' ')}`;

                const inputClass =
                  'block w-full px-3 py-1.5 text-sm bg-surface border border-slate-200 rounded-lg placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors';

                const locationProps = {
                  'aria-label': valueLabel,
                  isClearable: true,
                  isLoading: locationsLoading,
                  options:
                    fieldName === 'Dispatch Location'
                      ? dispatchLocationOptions
                      : receiptLocationOptions,
                  value: value ? { value, label: value } : null,
                  onChange: (opt: LocationOption | null) =>
                    handleFieldChange(fieldName, opt ? opt.value : ''),
                  placeholder: locationsLoading
                    ? 'Loading locations…'
                    : canCreateLocation
                      ? `Select or type a ${fieldName.toLowerCase()}…`
                      : `Select a ${fieldName.toLowerCase()}…`,
                  classNamePrefix: 'select',
                  styles: locationSelectStyles,
                  menuPortalTarget: typeof document !== 'undefined' ? document.body : null,
                  menuPosition: 'fixed' as const,
                };

                return (
                  <div
                    key={fieldName}
                    className={`flex items-start gap-4 px-3 -mx-3 py-2.5 rounded transition-colors ${isChecked ? 'bg-primary-50/60' : ''}`}
                  >
                    <input
                      type="checkbox"
                      id={`check-${fieldName}`}
                      checked={isChecked}
                      onChange={() => handleToggleField(fieldName)}
                      className="form-checkbox h-4 w-4 mt-0.5 text-primary-600 border-gray-300 rounded focus:ring-2 focus:ring-primary-500/40 focus:ring-offset-0 cursor-pointer"
                    />
                    <label
                      htmlFor={`check-${fieldName}`}
                      className={`w-44 flex-shrink-0 text-sm leading-5 pt-px cursor-pointer tracking-tight ${isChecked ? 'text-gray-900 font-medium' : 'text-gray-600'}`}
                    >
                      {label}
                    </label>
                    {isChecked && (
                      <div className="flex-1 min-w-0 space-y-1.5">
                        {TIMESTAMP_FIELDS.has(fieldName) ? (
                          <input
                            type="datetime-local"
                            aria-label={valueLabel}
                            value={formatDateTimeLocal(value)}
                            onChange={(e) => handleFieldChange(fieldName, e.target.value)}
                            className={`${inputClass} tabular-nums`}
                          />
                        ) : isWeightField(fieldName) ? (
                          <input
                            type="number"
                            step="any"
                            aria-label={valueLabel}
                            value={value}
                            onChange={(e) => handleFieldChange(fieldName, e.target.value)}
                            className={`${inputClass} tabular-nums`}
                          />
                        ) : LOCATION_FIELDS.has(fieldName) ? (
                          <>
                            {canCreateLocation ? (
                              <CreatableSelect<LocationOption, false>
                                {...locationProps}
                                formatCreateLabel={(input) => `Use "${input}" (new)`}
                              />
                            ) : (
                              <Select<LocationOption, false>
                                {...locationProps}
                                noOptionsMessage={() => 'No known location matches'}
                              />
                            )}
                            <div className="text-[11px] text-gray-500">
                              {scopedTicketType
                                ? `Options scoped to ${scopedTicketType} transactions`
                                : 'Mixed Ticket Types selected — showing all locations'}
                            </div>
                          </>
                        ) : (
                          <input
                            type="text"
                            aria-label={valueLabel}
                            value={value}
                            placeholder={
                              fieldName === 'Time Between Weights'
                                ? 'H:MM:SS, e.g. 0:04:22'
                                : undefined
                            }
                            onChange={(e) => handleFieldChange(fieldName, e.target.value)}
                            className={`${inputClass}${fieldName === 'Time Between Weights' ? ' tabular-nums' : ''}`}
                          />
                        )}
                        {/* Inline hint: current value(s) */}
                        {selectedTickets &&
                          selectedTickets.length > 0 &&
                          (() => {
                            const summary = summarizeFieldValues(fieldName, selectedTickets);
                            if (summary.length === 0) return null;
                            if (selectedTickets.length === 1) {
                              return (
                                <div
                                  className="flex items-baseline gap-1.5 text-[11px] text-gray-500 truncate"
                                  title={summary[0]!.display}
                                >
                                  <span className="uppercase tracking-[0.1em] text-gray-400 text-[10px] font-medium flex-shrink-0">
                                    Was
                                  </span>
                                  <span className="font-medium text-gray-700 truncate">
                                    {summary[0]!.display}
                                  </span>
                                </div>
                              );
                            }
                            const top = summary.slice(0, 3);
                            const rest = summary.slice(3);
                            const restTickets = rest.reduce((a, b) => a + b.count, 0);
                            return (
                              <div className="text-[11px] text-gray-500 leading-relaxed">
                                <span className="uppercase tracking-[0.1em] text-gray-400 text-[10px] font-medium mr-1.5">
                                  Current
                                </span>
                                {top.map((s, i) => (
                                  <span key={i} className="whitespace-nowrap">
                                    {i > 0 && <span className="text-gray-300 mx-1">·</span>}
                                    <span className="font-medium text-gray-700">{s.display}</span>
                                    <span className="text-gray-400 tabular-nums"> ×{s.count}</span>
                                  </span>
                                ))}
                                {rest.length > 0 && (
                                  <span className="text-gray-400 ml-1.5 tabular-nums">
                                    +{rest.length} more ({restTickets} ticket
                                    {restTickets === 1 ? '' : 's'})
                                  </span>
                                )}
                              </div>
                            );
                          })()}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>

          {/* Notes */}
          <section>
            <div className="flex items-baseline justify-between mb-2">
              <label
                htmlFor="bulkAmendmentNotes"
                className="text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-500"
              >
                Amendment Notes
                <span className="text-red-500 ml-1 normal-case tracking-normal">*</span>
              </label>
              <span className="text-[10px] text-gray-400 uppercase tracking-[0.1em]">Required</span>
            </div>
            <textarea
              id="bulkAmendmentNotes"
              name="bulkAmendmentNotes"
              rows={3}
              value={amendmentNotes}
              onChange={(e) => setAmendmentNotes(e.target.value)}
              placeholder="Explain the reason for this bulk amendment…"
              className={`block w-full px-3 py-2 text-sm bg-surface border rounded-md shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 transition-colors ${
                !amendmentNotes.trim() && error ? 'border-red-300 bg-red-50/30' : 'border-gray-300'
              }`}
            />
          </section>

          {/* Attachments — shown for single-ticket amendments */}
          {ticketCount === 1 && selectedTickets[0]?.TicketUID && (
            <section className="pt-2 border-t border-gray-200">
              <TicketAttachments ticketUID={selectedTickets[0].TicketUID} />
            </section>
          )}
        </div>

        {/* Footer */}
        <div className="px-7 py-4 border-t border-gray-200 bg-gray-50/80 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={handleClose}
            disabled={isLoading}
            className="inline-flex items-center rounded-md bg-surface px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 transition-colors"
          >
            {partiallySaved ? 'Close' : 'Cancel'}
          </button>
          {/* After a partial save the same update is not offered again: it would add another
              version to every ticket that did save. */}
          {!partiallySaved && (
            <button
              type="button"
              onClick={handleSave}
              disabled={!canSubmit}
              className="inline-flex items-center rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading
                ? 'Saving…'
                : `Apply Updates to ${ticketCount} Ticket${ticketCount === 1 ? '' : 's'}`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkAmendmentModalAllocateTransactions; // Renamed export
